import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ActivatedRoute } from '@angular/router';
import { Global } from '../../../models/global.model';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RightBridgeApiService } from '../../../services/right-bridge-api.service';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-form-repcode',
  styleUrls: ['form-repCode.component.scss'],
  template: `
    @if (props.label) {
      <label
        for="rep-code-{{ field.key }}"
        class="section w-100"
        tabindex="0"
        [class.mat-error]="formControl.errors && formControl.touched"
        [innerHTML]="props.label"
      >
        @if (props.required) {
          <sup>*</sup>
        }
      </label>
    }
    @if (props.prompt) {
      <p class="explanation" tabindex="0" [innerHTML]="props.prompt"></p>
    }
    <mat-form-field class="w-100">
      <mat-label>{{ loading ? 'Loading Rep Codes...' : to.label }}</mat-label>
      <input
        id="rep-code-{{ field.key }}"
        type="text"
        aria-label="Rep Code Search"
        matInput
        class="w-100"
        [placeholder]="props.placeholder || props.label"
        [formControl]="formControl"
        [matAutocomplete]="repAutocomplete"
        [required]="props.required"
        [formlyAttributes]="field"
        (blur)="checkSelection($event)"
      />
      <mat-autocomplete #repAutocomplete="matAutocomplete">
        @for (opt of repUnits; track opt.repCode) {
          <mat-option
            matTooltip="{{ opt.repCode }}"
            matToolTipPosition="before"
            matTooltipShowDelay="750"
            [value]="opt.repCode"
            >{{ opt.repName || opt.name }} ({{ opt.repCode }})</mat-option
          >
        }
      </mat-autocomplete>
      @if (
        formControl.hasError('required') && !formControl.hasError('regRequired')
      ) {
        <mat-error>This field is required</mat-error>
      }
      @if (formControl.hasError('regRequired')) {
        <mat-error
          >This field is needed for suitability review. You can opt out below if
          the applicant refuses to provide the information.</mat-error
        >
      }
    </mat-form-field>
  `,
  imports: [
    FormlyModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
})
export class FormlyRepCodeComponent extends FieldType implements OnInit {
  private ss = inject(SessionStorageService);
  private route = inject(ActivatedRoute);
  private rbs = inject(RightBridgeApiService);
  private changeDetector = inject(ChangeDetectorRef);

  globals: Global;
  disabledDrop = false;
  appStatus = false;
  profile = '';
  loading = false;
  filteredReps: Observable<string[]>;
  repUnits: {
    repCode: string;
    repName: string;
    name?: string;
    unitId?: string;
    unitName?: string;
    unitType?: string;
  }[];
  rawRepCodes: {
    repCode: string;
    repName: string;
    name?: string;
    unitId?: string;
    unitName?: string;
    unitType?: string;
  }[];
  requestRepsSearch: boolean;
  repFieldSelected: boolean;

  ngOnInit() {
    this.loading = true;
    this.globals = this.ss.get('globals');
    this.rawRepCodes = this.ss.get('repUnits');

    this.route.params.subscribe(params => {
      this.profile = params.id ? params.id : null;
    });
    if (
      this.globals &&
      this.globals.user.managerUnitCount &&
      this.globals.user.managerUnitCount > 0
    ) {
      this.requestRepsSearch = true;
    } else {
      this.requestRepsSearch = false;
    }

    if (Array.isArray(this.formControl.value)) {
      // repcode should be a single string. rawValue is usually an array of one string.
      this.formControl.setValue(this.formControl.value[0]);
    }

    this.repFieldSelected = !!this.model['ClientProfilingOut_RepCodeSelected'];
    // NOTE: A rep code should always be selected in the questionnaire call to prevent lost cases,
    // but this var controls if the user explicity set that a rep code is selected to be used for a case.
    // This setting is set on the patch save (next button).

    // CONDITIONS:
    // repcode has not been explicitly set by the user/integration
    // -> clear the field to motivate them to select.
    // Saving the questionnaire should set the var to true.
    // (bypassing the following code to clear the field)
    // (the assumption here is the user/integration has explicitly set the repcode
    // on this case, and thus the var on the model that indicates that)
    if (!this.repFieldSelected) {
      this.formControl.setValue(null);
    }
    // else the field will have the currently selected repcode id
    // from the value of this question.

    if (this.requestRepsSearch) {
      //managed units user -> set up for searching. search on default if set (avoids the blur clearing).
      if (this.formControl.value) this._filterReps(this.formControl.value);
      this.setUpTypeAhead();
    } else {
      //no managed units -> set up dropdown options and
      //determine behavior based on number of repcodes available
      this.setUpDropDown();
    }
  }

  setUpTypeAhead() {
    this.loading = false;
    this.formControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && typeof value === 'string' && value.length > 0) {
          return this._filterReps(value);
        } else {
          if (this.requestRepsSearch) {
            this.repUnits = [];
          } else {
            this.repUnits = this.rawRepCodes;
          }
          this.changeDetector.detectChanges();
        }
      });
  }

  private _filterReps(value: string) {
    if (this.requestRepsSearch) {
      this.rbs.getRepCodes(value || '%').subscribe(res => {
        if (res.names && res.names.length > 0) {
          this.repUnits = res.names;
          this.changeDetector.detectChanges();
        } else if (!res?.names || res.names.length === 0) {
          this.repUnits = [];
          this.changeDetector.detectChanges();
        }
      });
    } else {
      this.repUnits = this.rawRepCodes.filter(
        rep =>
          rep.repName.toLowerCase().includes(value.toLowerCase()) ||
          rep.repCode.toLowerCase().includes(value.toLowerCase())
      );
      if (this.repUnits.length === 0) {
        this.repUnits = [];
      }
      this.changeDetector.detectChanges();
    }
  }

  checkSelection(ev) {
    if (
      !this.repUnits ||
      !this.repUnits.find(repUnit => {
        return repUnit.repCode === this.formControl.value;
      })
    ) {
      this.formControl.setValue(null);
    }
  }

  setUpDropDown() {
    if (!this.rawRepCodes || this.rawRepCodes.length === 0) {
      this.rbs.getRepCodeList().subscribe(res => {
        this.rawRepCodes = res;
        this.repCodeEval();
      });
    } else {
      this.repCodeEval();
    }
    this.setUpTypeAhead();
  }

  repCodeEval() {
    this.repUnits = this.rawRepCodes.map(x => {
      return { repCode: x.repCode, repName: x.repName };
    });
    if (this.repUnits.length < 1) {
      //if rep code list is empty set to raw value and lock
      //(error case but this is here to handle it. if there is
      //no raw value though, that's also an error)
      this.repUnits = [
        {
          repCode: this.props.rawValues['rawValue'],
          repName: this.props.rawValues['display'],
        },
      ];
      this.formControl.setValue(this.repUnits[0].repCode);
      this.formControl.disable();
    } else if (this.repUnits.length === 1) {
      //else if rep code list has only one value. set and lock
      this.formControl.setValue(this.repUnits[0].repCode);
      this.formControl.disable();
    } else {
      //else (not locked in yet and multiple codes to choose)
      if (!this.repFieldSelected) {
        this.formControl.setValue(null);
      }
    }
    this.loading = false;
    this.changeDetector.detectChanges();
  }
}
